import axios from "axios";
import type { CreateUser, UpdateUser, Exam, CreateExam, Question, GenerateExamRequest, GenerateExamResponse, Attempt, UserAnswer, Settings, User } from "src/types/types";
import { auth, signInWithEmailAndPassword, signInWithPopup, googleProvider } from "src/api/firebase";
import type { User as FirebaseUser } from "firebase/auth";

export const API = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000',
  timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 1000000
});

axios.defaults.withCredentials = true

API.interceptors.request.use(async (config) => {
  const token = await auth.currentUser?.getIdToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
const getUser = async (id: string) => {
  const response = await API.get(`/users/${id}`)
  return response.data
}
const createUser = async (user: CreateUser) => {
  const response = await API.post('/users', user)
  return response.data
}

const updateUser = async (user: UpdateUser) => {
  const response = await API.put('/users', user)
  return response.data
}

const loginWithEmail = async (email: string, password: string): Promise<FirebaseUser> => {
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user;
}

const loginWithGoogle = async (): Promise<FirebaseUser> => {
  const result = await signInWithPopup(auth, googleProvider);
  return result.user;
}

const getExams = async (): Promise<Exam[]> => {
  const response = await API.get('/exams');
  return response.data.data;
}

const getExamById = async (examId: string): Promise<Exam> => {
  const response = await API.get(`/exams/${examId}`);
  return response.data.data;
}

const createExam = async (data: CreateExam): Promise<Exam> => {
  const response = await API.post('/exams', data);
  return response.data.data;
}

const getExamQuestions = async (examId: string): Promise<Question[]> => {
  const response = await API.get(`/exams/${examId}/questions`);
  return response.data.data;
}

const generateExam = async (data: GenerateExamRequest): Promise<GenerateExamResponse> => {
  const response = await API.post('/exams/generate', data);
  return response.data.data;
}

const startAttempt = async (examId: string, userId: string, note?: string): Promise<Attempt> => {
  const response = await API.post('/attempts', { examId, userId, note });
  return response.data.data;
}

const getAttempt = async (attemptId: string): Promise<Attempt> => {
  const response = await API.get(`/attempts/${attemptId}`);
  return response.data.data;
}

const saveAnswer = async (attemptId: string, answer: UserAnswer): Promise<Attempt> => {
  const response = await API.patch(`/attempts/${attemptId}/answer`, answer);
  return response.data.data;
}

const submitAttempt = async (attemptId: string): Promise<Attempt> => {
  const response = await API.post(`/attempts/${attemptId}/submit`);
  return response.data.data;
}

const getAttempts = async (userId: string): Promise<Attempt[]> => {
  const response = await API.get('/attempts', { params: { userId } });
  return response.data.data;
}

const deleteExam = async (examId: string, userId: string): Promise<void> => {
  await API.delete(`/exams/${examId}`, { params: { userId } });
}

const deleteAttempt = async (attemptId: string, userId: string): Promise<void> => {
  await API.delete(`/attempts/${attemptId}`, { params: { userId } });
}

const getAdminUsers = async (): Promise<User[]> => {
  const response = await API.get('/admin/users');
  return response.data.data;
}

const getAdminAttempts = async (): Promise<Attempt[]> => {
  const response = await API.get('/admin/attempts');
  return response.data.data;
}

const getSettings = async (): Promise<Settings> => {
  const response = await API.get('/settings');
  return response.data.data;
}

const updateSettings = async (data: Partial<Settings>): Promise<Settings> => {
  const response = await API.put('/settings', data);
  return response.data.data;
}

export default {
  getUser,
  createUser,
  updateUser,
  loginWithEmail,
  loginWithGoogle,
  getExams,
  getExamById,
  createExam,
  getExamQuestions,
  generateExam,
  startAttempt,
  getAttempt,
  getAttempts,
  saveAnswer,
  submitAttempt,
  deleteExam,
  deleteAttempt,
  getAdminUsers,
  getAdminAttempts,
  getSettings,
  updateSettings,
}
